import styled from 'styled-components';
import {getColorFromTheme} from '@ui/ui-utils';

export const Container = styled.div<{$compact?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${({$compact}) => ($compact ? '12px' : '16px')};
  width: 100%;
  max-width: 527px;
  margin: 0 auto;
`;

export const CadenceCard = styled.section`
  overflow: visible;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const CardHeader = styled.div<{$compact?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: ${({$compact}) => ($compact ? '14px 16px' : '20px 24px')};
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
`;

export const CadenceCardBody = styled.div<{$compact?: boolean}>`
  padding: ${({$compact}) => ($compact ? '16px' : '24px')};
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SendOnSection = styled.section`
  display: flex;
  flex-direction: column;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const SendOnHeader = styled.div<{$compact?: boolean}>`
  padding: ${({$compact}) => ($compact ? '10px 12px' : '12px 16px')};
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
`;

export const SendOnContent = styled.div<{$compact?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: ${({$compact}) => ($compact ? '12px' : '16px')};
`;

export const StartEndStack = styled.div<{$compact?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${({$compact}) => ($compact ? '6px' : '8px')};
`;

export const StartDateSection = styled.section`
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
`;

export const EndDateSection = styled(StartDateSection)``;

export const SectionSubHeader = styled.div<{$compact?: boolean}>`
  padding: ${({$compact}) => ($compact ? '10px 12px' : '12px 16px')};
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
`;

export const SectionContent = styled.div<{$compact?: boolean}>`
  padding: ${({$compact}) => ($compact ? '12px' : '16px')};
`;

const dateButtonStyles = `
  appearance: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

export const SimpleDatePicker = styled.button<{$open?: boolean}>`
  ${dateButtonStyles};
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  text-align: left;

  &:hover {
    border-color: ${getColorFromTheme('purple300')};
  }

  &:focus {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
  }

  ${({$open}) => $open && `border-color: ${getColorFromTheme('purple300')};`}

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const CalendarIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const DatePickerDropdown = styled.div`
  position: absolute;
  z-index: 20;
  top: calc(100% + 8px);
  left: 0;
  border-radius: 10px;

  @media (max-width: 520px) {
    right: 0;
    left: auto;
  }
`;

export const CompactDatePicker = styled.button<{$open?: boolean}>`
  ${dateButtonStyles};
  width: 177px;
  height: 40px;
  padding: 8px 18px;
  gap: 16px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('textBody')};
  text-align: left;

  &:hover {
    border-color: ${getColorFromTheme('purple300')};
  }

  &:focus {
    outline: none;
    border-color: ${getColorFromTheme('purple300')};
  }

  ${({$open}) => $open && `border-color: ${getColorFromTheme('purple300')};`}

  &:focus-visible {
    outline: 2px solid ${getColorFromTheme('purple300')};
    outline-offset: 2px;
  }
`;

export const CompactCalendarIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const MonthlyOptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const MonthlyOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 40px;

  @media (max-width: 520px) {
    flex-wrap: wrap;
  }
`;

export const MonthlyRadioLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${getColorFromTheme('blackTwo')};
  cursor: pointer;
  white-space: nowrap;
`;

export const MonthlyInlineSelect = styled.div<{width?: string}>`
  width: ${({width}) => width || '96px'};
  flex: 0 0 ${({width}) => width || '96px'};

  & > div {
    margin: 0;
  }
`;

export const TimeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const TimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 520px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const TimeSelectContainer = styled.div`
  display: inline-flex;
  align-items: center;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 10px;
  padding: 16px;
  width: fit-content;

  & .body {
    gap: 6px !important;
  }

  & .input {
    width: 56px !important;
  }

  @media (max-width: 520px) {
    width: 100%;
  }
`;

export const ManagerTimeNote = styled.div`
  color: ${getColorFromTheme('textBody')};
`;

export const RadioRowWithField = styled.div`
  display: grid;
  grid-template-columns: 169px minmax(0, 1fr);
  align-items: center;
  gap: 16px;

  .radio-option {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

export const OccurrencesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  input {
    width: 77px;
  }
`;

export const DayGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
`;

const dayControlStyles = `
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 27px;
  cursor: pointer;
`;

export const DayCheckboxLabel = styled.label`
  ${dayControlStyles};
`;

export const DayRadioLabel = styled.label`
  ${dayControlStyles};
`;

export const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  .radio-option {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }
`;
